/**
 * Technical basis and references.
 *
 * Each entry records bibliographic identification and — separately — what was
 * actually examined when the entry was written. No normative clause text is
 * reproduced. Entries are data so they can be updated when licensed standards
 * and further literature are reviewed directly.
 *
 * Bibliographic details below were taken from publicly accessible catalogue
 * records (publisher and standards-store listings). Catalogue records identify
 * a document; they are not the document.
 */

import type { EvidenceTier, Reference } from '../types';

export const TIER_LABELS: Record<EvidenceTier, string> = {
  'authoritative-standard-not-independently-reviewed':
    'Authoritative standard — full licensed text not reviewed for this build',
  'authoritative-source-accessible': 'Authoritative source — publicly accessible record reviewed',
  'secondary-source': 'Secondary source',
  'practitioner-guidance': 'Practitioner guidance',
  'adjacent-domain-evidence': 'Adjacent-domain evidence',
  interpretation: 'Interpretation',
  hypothesis: 'Hypothesis',
  'unverified-normative-claim': 'Unverified normative claim',
  'specification-derived': 'Derived from this project’s build specification',
  'pending-source-document': 'Pending — source document not supplied to this build',
};

export const REFERENCES: Reference[] = [
  {
    id: 'astm-f1980-21',
    designation: 'ASTM F1980-21',
    title: 'Standard Guide for Accelerated Aging of Sterile Barrier Systems and Medical Devices',
    publisher: 'ASTM International',
    year: '2021',
    tier: 'authoritative-standard-not-independently-reviewed',
    reviewStatus:
      'Title and publication details taken from publicly accessible catalogue records. The full licensed text was not reviewed for this build. No clause content is reproduced or paraphrased here.',
    url: 'https://store.astm.org/f1980-21.html',
    note: 'Obtain a controlled copy of the current edition before relying on any requirement.',
  },
  {
    id: 'astm-wk94053',
    designation: 'ASTM WK94053',
    title: 'Work item: revision of F1980-21 (subcommittee F02.50)',
    publisher: 'ASTM International',
    year: 'In development',
    tier: 'authoritative-source-accessible',
    reviewStatus:
      'The existence and subject of the work item are listed on a publicly accessible ASTM page. A work item is a proposal under development, not an adopted requirement, and its status may have changed since this entry was written.',
    url: 'https://www.astm.org/membership-participation/technical-committees/workitems/workitem-wk94053',
    note: 'Included because it shows the Q10 basis is an area of active technical discussion, not a settled constant.',
  },
  {
    id: 'iso-11607-1-2019',
    designation: 'ISO 11607-1:2019',
    title:
      'Packaging for terminally sterilized medical devices — Part 1: Requirements for materials, sterile barrier systems and packaging systems',
    publisher: 'International Organization for Standardization',
    year: '2019',
    tier: 'authoritative-standard-not-independently-reviewed',
    reviewStatus:
      'Title, scope abstract and confirmation status taken from the publisher’s public catalogue record. The full standard was not reviewed for this build. The catalogue record also lists an amendment — confirm the current edition and amendment status directly.',
    url: 'https://www.iso.org/standard/70799.html',
  },
  {
    id: 'iso-11607-2-2019',
    designation: 'ISO 11607-2:2019',
    title:
      'Packaging for terminally sterilized medical devices — Part 2: Validation requirements for forming, sealing and assembly processes',
    publisher: 'International Organization for Standardization',
    year: '2019',
    tier: 'authoritative-standard-not-independently-reviewed',
    reviewStatus:
      'Title and scope abstract taken from the publisher’s public catalogue record. The full standard was not reviewed for this build. An amendment is listed in the catalogue record — confirm current status directly.',
    url: 'https://www.iso.org/standard/70800.html',
  },
  {
    id: 'fda-shelf-life-1991',
    designation: 'FDA — “Shelf Life of Medical Devices”',
    title: 'Guidance document on shelf life of medical devices',
    publisher: 'U.S. Food and Drug Administration, CDRH',
    year: 'Originally issued 1991 (per secondary sources)',
    tier: 'secondary-source',
    reviewStatus:
      'Identified through secondary sources that reference it. The primary document was not retrieved or reviewed for this build, and its current status was not confirmed. Verify existence, currency and applicability on the FDA website before citing it.',
    note: 'Listed as a lead to follow up, not as a confirmed authority for this tool.',
  },
  {
    id: 'polymer-aging-literature',
    designation: 'Peer-reviewed polymer aging literature',
    title: 'Placeholder — specific citations pending',
    publisher: '—',
    year: '—',
    tier: 'pending-source-document',
    reviewStatus:
      'No specific papers are listed. The evidence map that would supply these citations was not available to this build. Inventing plausible-looking citations here would be worse than leaving the slot visibly empty, so it is left visibly empty.',
    note:
      'Populate from the evidence map’s source register, including how each paper relates to Q10 behaviour, glass-transition effects, and Arrhenius applicability limits.',
  },
];

export const REFERENCES_QUALIFICATION =
  'Current licensed standards should be reviewed directly for normative requirements. References shown here provide technical context and traceability but do not replace controlled copies of applicable standards.';
